import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Plus, Trash2, Send, Mail, ShieldAlert } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { adminApi, publicApi } from '@/lib/api';
import { ADMIN } from '@/constants/testIds';
import { useSeo } from '@/lib/seo';

function OpenerRow({ opener, onChange, onRemove, index }) {
  return (
    <div className="grid gap-3 sm:grid-cols-[1fr_180px_auto] sm:items-end">
      <div>
        <Label className="kicker mb-1.5 block">Label</Label>
        <Input
          data-testid={`opener-label-${index}`}
          value={opener.label}
          onChange={(e) => onChange({ ...opener, label: e.target.value })}
          placeholder="Archery opener"
        />
      </div>
      <div>
        <Label className="kicker mb-1.5 block">Date</Label>
        <Input
          data-testid={`opener-date-${index}`}
          type="date"
          value={opener.date ? opener.date.slice(0, 10) : ''}
          onChange={(e) => onChange({ ...opener, date: e.target.value })}
        />
      </div>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={onRemove}
        aria-label="Remove opener"
        data-testid={`opener-remove-${index}`}
      >
        <Trash2 size={14} />
      </Button>
    </div>
  );
}

export default function SiteConfigPage() {
  useSeo({ title: 'Site Config | Admin' });
  const [form, setForm] = useState({
    social_youtube: '',
    social_instagram: '',
    social_tiktok: '',
    social_facebook: '',
    contact_email: 'hello@aguywhohunts.com',
    gear_nav_enabled: false,
    sender_email: '',
    openers: [],
  });
  const [saving, setSaving] = useState(false);
  const [emailStatus, setEmailStatus] = useState(null);
  const [testOpen, setTestOpen] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [testing, setTesting] = useState(false);

  const loadEmailStatus = () => adminApi.emailStatus().then(setEmailStatus).catch(() => {});

  useEffect(() => {
    publicApi.siteConfig().then((d) => {
      if (d)
        setForm({
          social_youtube: d.social_youtube || '',
          social_instagram: d.social_instagram || '',
          social_tiktok: d.social_tiktok || '',
          social_facebook: d.social_facebook || '',
          contact_email: d.contact_email || 'hello@aguywhohunts.com',
          gear_nav_enabled: Boolean(d.gear_nav_enabled),
          sender_email: d.sender_email || '',
          openers: Array.isArray(d.openers) ? d.openers : [],
        });
    });
    loadEmailStatus();
  }, []);

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const cleanOpeners = (form.openers || [])
        .map((o) => ({
          label: (o.label || '').trim(),
          date: (o.date || '').trim(),
        }))
        .filter((o) => o.label && o.date);
      await adminApi.updateConfig({ ...form, openers: cleanOpeners });
      toast.success('Site config saved.');
      loadEmailStatus();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const addOpener = () =>
    set({ openers: [...(form.openers || []), { label: '', date: '' }] });
  const updateOpener = (idx, next) =>
    set({ openers: form.openers.map((o, i) => (i === idx ? next : o)) });
  const removeOpener = (idx) =>
    set({ openers: form.openers.filter((_, i) => i !== idx) });

  const openTestDialog = () => {
    setTestEmail(emailStatus?.owner_email || '');
    setTestOpen(true);
  };

  const sendTest = async () => {
    setTesting(true);
    try {
      const payload = testEmail ? { to: testEmail } : {};
      const res = await adminApi.sendTestEmail(payload);
      toast.success(res.message || `Sent to ${res.sent_to}`);
      setTestOpen(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Test send failed');
    } finally {
      setTesting(false);
    }
  };

  const isSandbox = emailStatus?.is_sandbox;

  return (
    <form onSubmit={save} className="space-y-6 max-w-3xl">
      <div>
        <p className="kicker">Site Config</p>
        <h1 className="font-serif text-3xl tracking-tight">
          Social, contact, email, and season openers.
        </h1>
      </div>

      {/* Social */}
      <Card className="border-[hsl(var(--border))]">
        <CardContent className="p-6 grid gap-5">
          <div>
            <p className="kicker">Social &amp; contact</p>
            <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1">
              These show up in the footer and the contact page.
            </p>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <Label className="kicker mb-2 block">YouTube URL</Label>
              <Input value={form.social_youtube} onChange={(e) => set({ social_youtube: e.target.value })} />
            </div>
            <div>
              <Label className="kicker mb-2 block">Instagram URL</Label>
              <Input value={form.social_instagram} onChange={(e) => set({ social_instagram: e.target.value })} />
            </div>
            <div>
              <Label className="kicker mb-2 block">TikTok URL</Label>
              <Input value={form.social_tiktok} onChange={(e) => set({ social_tiktok: e.target.value })} />
            </div>
            <div>
              <Label className="kicker mb-2 block">Facebook URL</Label>
              <Input value={form.social_facebook} onChange={(e) => set({ social_facebook: e.target.value })} />
            </div>
          </div>
          <div>
            <Label className="kicker mb-2 block">Contact email (displayed on public site)</Label>
            <Input
              value={form.contact_email}
              onChange={(e) => set({ contact_email: e.target.value })}
            />
          </div>
          <div className="flex items-center gap-3">
            <Switch
              id="gear-toggle"
              checked={form.gear_nav_enabled}
              onCheckedChange={(v) => set({ gear_nav_enabled: Boolean(v) })}
            />
            <Label htmlFor="gear-toggle" className="font-medium">
              Show Gear in navigation
            </Label>
          </div>
        </CardContent>
      </Card>

      {/* Email */}
      <Card className="border-[hsl(var(--border))]">
        <CardContent className="p-6 grid gap-5">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <p className="kicker">Email sending</p>
              <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1">
                Sender address used for welcome emails, contact alerts, and recap broadcasts.
              </p>
            </div>
            {emailStatus && (
              <Badge
                variant="secondary"
                className={`font-normal ${isSandbox ? 'text-amber-700 border border-amber-400/50' : ''}`}
                data-testid="email-status-badge"
              >
                {isSandbox ? 'Sandbox sender' : 'Live sender'}
              </Badge>
            )}
          </div>

          {isSandbox && (
            <div className="flex items-start gap-2 rounded-md border border-amber-400/40 bg-amber-50/60 p-3 text-sm text-amber-900">
              <ShieldAlert size={16} className="mt-0.5 shrink-0" />
              <div>
                <p className="font-medium">You&apos;re on Resend&apos;s shared sandbox sender.</p>
                <p className="text-amber-800/90">
                  Only your own verified Resend account inbox will actually receive mail. To send
                  to real subscribers, verify <code>aguywhohunts.com</code> in Resend, then paste
                  a from-address below (e.g. <code>hello@aguywhohunts.com</code>).
                </p>
              </div>
            </div>
          )}

          <div>
            <Label className="kicker mb-2 block" htmlFor="sender-email">
              Sender email (leave blank to use the backend default)
            </Label>
            <Input
              id="sender-email"
              data-testid="sender-email-input"
              value={form.sender_email}
              onChange={(e) => set({ sender_email: e.target.value })}
              placeholder="A Guy Who Hunts <hello@aguywhohunts.com>"
            />
            <p className="mt-1.5 text-xs text-[hsl(var(--muted-foreground))]">
              Currently effective:{' '}
              <code>{emailStatus?.sender_email || '—'}</code>
            </p>
          </div>

          <div>
            <Button
              type="button"
              variant="secondary"
              onClick={openTestDialog}
              data-testid="send-test-email-button"
              disabled={!emailStatus?.enabled}
            >
              <Send size={14} className="mr-1.5" /> Send test email
            </Button>
            {!emailStatus?.enabled && (
              <p className="mt-1.5 text-xs text-[hsl(var(--muted-foreground))]">
                Set <code>RESEND_API_KEY</code> in the backend env to enable this.
              </p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Openers */}
      <Card className="border-[hsl(var(--border))]">
        <CardContent className="p-6 grid gap-5">
          <div>
            <p className="kicker">Season openers</p>
            <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1">
              Add key dates like <em>Archery opener</em>, <em>Firearm opener</em>. The home page
              shows a countdown to the nearest upcoming date. Past dates are ignored.
            </p>
          </div>
          <div className="space-y-4">
            {(form.openers || []).length === 0 && (
              <p className="text-sm text-[hsl(var(--muted-foreground))] italic">
                No openers yet. Add one below and the countdown appears on the home page.
              </p>
            )}
            {(form.openers || []).map((o, i) => (
              <div key={i}>
                <OpenerRow
                  opener={o}
                  index={i}
                  onChange={(next) => updateOpener(i, next)}
                  onRemove={() => removeOpener(i)}
                />
                {i < form.openers.length - 1 && <Separator className="mt-4" />}
              </div>
            ))}
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={addOpener}
              data-testid="opener-add-button"
            >
              <Plus size={14} className="mr-1.5" /> Add opener
            </Button>
          </div>
        </CardContent>
      </Card>

      <div className="pt-2">
        <Button type="submit" disabled={saving} data-testid={ADMIN.configSave}>
          {saving ? 'Saving…' : 'Save configuration'}
        </Button>
      </div>

      <Dialog open={testOpen} onOpenChange={setTestOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Send a test email</DialogTitle>
            <DialogDescription>
              We&apos;ll dispatch a small branded email from the effective sender so you can
              confirm delivery. In sandbox mode only your own Resend account inbox will receive
              it.
            </DialogDescription>
          </DialogHeader>
          <div>
            <Label className="kicker mb-2 block" htmlFor="test-to">
              Send to
            </Label>
            <Input
              id="test-to"
              type="email"
              value={testEmail}
              onChange={(e) => setTestEmail(e.target.value)}
              placeholder="you@example.com"
              data-testid="test-email-to-input"
            />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setTestOpen(false)} disabled={testing}>
              Cancel
            </Button>
            <Button onClick={sendTest} disabled={testing} data-testid="test-email-send-button">
              <Mail size={14} className="mr-1.5" />
              {testing ? 'Sending…' : 'Send test'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </form>
  );
}
